import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, asset, formatUSD } from "../lib/api";
import { ScrollScrubViewer } from "../components/ScrollScrubViewer";
import { PrintOrderModal } from "../components/PrintOrderModal";
import { ShareModal } from "../components/ShareModal";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

export default function TimelapseDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const initialFrame = parseInt(params.get("frame") || "0", 10) || 0;

  const [tl, setTl] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [printCtx, setPrintCtx] = useState(null);
  const [share, setShare] = useState(null);
  const [licenseType, setLicenseType] = useState(null);

  useEffect(() => {
    api.get(`/timelapses/${id}`).then(({ data }) => {
      setTl(data);
      document.title = `${data.title} — ${data.location} — Scott Burton Photography`;
      setMeta("og:title", data.title);
      setMeta("og:description", data.description);
      if (data.thumbnail_url) setMeta("og:image", asset(data.thumbnail_url));
      setMeta("description", data.description, "name");
    });
    api.get("/pricing").then(({ data }) => setCatalog(data));
  }, [id]);

  if (!tl) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading sequence…</div>;
  if (tl.status === "processing") return (
    <div className="min-h-screen flex flex-col items-center justify-center text-neutral-400">
      <Loader2 className="animate-spin text-amber-glow" size={36} />
      <p className="mt-4">This sequence is still processing. Check back in a moment.</p>
    </div>
  );

  const shareUrl = (idx) => `${window.location.origin}/timelapse/${tl.slug || tl.id}?frame=${idx}`;

  const meta = [
    ["Camera", tl.camera], ["Lens", tl.lens], ["Interval", tl.interval],
    ["Shots", tl.shot_count], ["Coordinates", tl.coordinates], ["Date", tl.date],
    ["Frames", tl.frame_count], ["Location", tl.location],
  ].filter(([, v]) => v);

  return (
    <div>
      <ScrollScrubViewer
        frames={tl.frames}
        title={tl.title}
        location={tl.location}
        startLabel={tl.capture_start_label}
        endLabel={tl.capture_end_label}
        initialFrame={initialFrame}
        onShare={(idx) => setShare(shareUrl(idx))}
        onOrder={({ index, dataUrl, timestamp }) =>
          setPrintCtx({ kind: "frame", title: tl.title, timelapseId: tl.id, frameIndex: index, timestamp, dataUrl })}
      />

      {/* below the fold */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="overline-label mb-3">{tl.category}</div>
        <p className="font-serif text-3xl lg:text-4xl font-light max-w-3xl leading-snug">{tl.description}</p>

        {/* licensing */}
        <div className="mt-16 grid md:grid-cols-2 gap-6">
          {[["personal", "Personal License", tl.personal_price, "Home display, personal projects and social — non-commercial, worldwide, forever."],
            ["commercial", "Commercial License", tl.commercial_price, "Advertising, film, broadcast and client work. Worldwide rights, tailored terms."]].map(([key, title, price, desc]) => (
            <div key={key} data-testid={`license-card-${key}`} className="border border-white/10 rounded-lg p-8 bg-obsidian-800 flex flex-col">
              <div className="overline-label">{key === "commercial" ? "For business" : "For you"}</div>
              <h3 className="font-serif text-3xl font-light mt-2">{title}</h3>
              <p className="text-neutral-400 text-sm mt-3 flex-1">{desc}</p>
              <div className="mt-6 flex items-center justify-between">
                <span className="font-mono text-2xl text-amber-glow">{formatUSD(price)}</span>
                <button data-testid={`license-buy-${key}`} onClick={() => setLicenseType(key)}
                  className="btn-amber rounded-full px-5 py-2.5 text-sm">Purchase license</button>
              </div>
            </div>
          ))}
        </div>

        {/* metadata */}
        <div className="mt-16">
          <div className="overline-label mb-6">Capture details</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/8 border border-white/8 rounded-lg overflow-hidden">
            {meta.map(([k, v]) => (
              <div key={k} className="bg-obsidian-800 p-5">
                <div className="font-mono text-[0.62rem] tracking-wider text-neutral-500 uppercase">{k}</div>
                <div className="mt-1 text-sm">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PrintOrderModal open={!!printCtx} onClose={() => setPrintCtx(null)} catalog={catalog} context={printCtx} />
      <ShareModal open={!!share} onClose={() => setShare(null)} url={share || ""} title={tl.title} />
      <LicenseDialog tl={tl} type={licenseType} onClose={() => setLicenseType(null)} />
    </div>
  );
}

function setMeta(key, value, attr = "property") {
  if (!value) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) { tag = document.createElement("meta"); tag.setAttribute(attr, key); document.head.appendChild(tag); }
  tag.setAttribute("content", value);
}

const LicenseDialog = ({ tl, type, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", usage_notes: "" });
  const [loading, setLoading] = useState(false);
  if (!type) return null;
  const price = type === "commercial" ? tl.commercial_price : tl.personal_price;

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/license", {
        timelapse_id: tl.id, license_type: type, customer_name: form.name,
        customer_email: form.email, company: form.company, usage_notes: form.usage_notes,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error("Could not start checkout."); setLoading(false); }
  };

  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="license-dialog" className="max-w-lg bg-obsidian-800 border-white/10">
        <DialogTitle className="sr-only">{tl.title} license</DialogTitle>
        <div className="overline-label">{type} license</div>
        <h3 className="font-serif text-3xl font-light mt-1">{tl.title}</h3>
        <div className="flex items-center gap-2 mt-2 text-amber-glow font-mono text-xl">{formatUSD(price)}</div>
        <div className="mt-5 space-y-3">
          <Input data-testid="license-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="license-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          {type === "commercial" && (
            <>
              <Input data-testid="license-company" placeholder="Company / brand" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <Textarea data-testid="license-usage" placeholder="Where will this be used? (media, territory, duration)" rows={3} value={form.usage_notes} onChange={(e) => setForm({ ...form, usage_notes: e.target.value })} />
            </>
          )}
        </div>
        <button data-testid="license-checkout" onClick={submit} disabled={loading} className="w-full mt-5 h-12 rounded-full btn-amber flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <Check size={16} />}
          {loading ? "Redirecting…" : "Continue to checkout"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
